import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Case, CaseStateAction, OperatorProps } from '../../../../@types/TemplateOperators';
import { ArgsInput, ArgsSelect } from './BasicComponents';
import { Button } from '../../../Button';
import { useSwitchConfig, useTemplateSteps, useTemplateStepsPreview } from '../../../../hooks/useTemplateList';
import { StepHolder } from '../StepHolder';
import { TemplateEntityContext } from '../../../../context/TemplateEntityContext';

const options = [
  { label: 'Equal To', value: 'eq' },
  { label: 'Not Equals', value: 'ne' },
  { label: 'Less Than', value: 'lt' },
  { label: 'Less Than or Equal', value: 'lte' },
  { label: 'Greater Than', value: 'gt' },
  { label: 'Greater Than or Equal', value: 'gte' }
];

const inputTypeOptions = [
  { label: 'Constant', value: 'constant' },
  { label: 'Advanced', value: 'advanced' }
];

const AdvancedSwitch: FC<OperatorProps> = ({ step, handleArgsChange }) => {
  const [state, dispatch] = useSwitchConfig(step.args[0]);
  const [defaultType, setDefaultType] = useState(Array.isArray(state.default) ? 'advanced' : 'constant');
  const [defaultSteps, dispatchDefaultSteps] = useTemplateSteps(Array.isArray(state.default) ? state.default : []);
  const {
    referenceData: { rows: referenceRows },
    firstDataLine,
    fieldOptions: dropDownOptions
  } = useContext(TemplateEntityContext);
  const { data: preview } = useTemplateStepsPreview(firstDataLine, defaultSteps, referenceRows);

  useEffect(() => {
    handleArgsChange([state]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    if (defaultType === 'advanced') {
      dispatch({ type: 'setDefault', val: defaultSteps });
    }
  }, [defaultSteps, defaultType, dispatch]);

  const handleSwitchConfigChange = useCallback(
    ({ type, idx }) => {
      return (val?: any) => {
        dispatch({ type, idx, val });
      };
    },
    [dispatch]
  );

  return (
    <>
      {state.cases.map((caseItem, idx) => (
        // eslint-disable-next-line react/jsx-key
        <CaseHolder
          caseItem={caseItem}
          idx={idx}
          dispatch={dispatch}
          handleSwitchConfigChange={handleSwitchConfigChange}
        />
      ))}
      <div className="flex flex-col mt-6 w-1/2">
        Default
        <ArgsSelect
          maxVisible={2}
          options={inputTypeOptions}
          onChange={(val) => setDefaultType(val.toString())}
          value={defaultType}
        />
        <div className="mb-5">
          {defaultType === 'constant' ? (
            <div data-testid="advanced-switch-constant-input" className="flex flex-row mt-0 w-full mr-0 justify-end">
              <ArgsInput
                initialValue={Array.isArray(state.default) ? '' : (state.default as string)}
                onChange={handleSwitchConfigChange({ type: 'setDefault' })}
              />
            </div>
          ) : (
            <div data-testid="advanced-switch-step-holder">
              {defaultSteps.map((step, idx) => (
                <StepHolder
                  key={`advanced-${idx}-${step.operation}-${step.args[0]}`}
                  index={idx}
                  step={step}
                  onUpdate={(data) => dispatchDefaultSteps({ type: 'update', index: idx, data })}
                  onRemove={() => dispatchDefaultSteps({ type: 'remove', index: idx })}
                  dropDownOptions={dropDownOptions}
                  preview={preview?.steps[idx] || 'No Preview'}
                  ignoreAdvanced
                />
              ))}
              <div className="flex gap-4">
                <Button id="add-step-advanced" onClick={() => dispatchDefaultSteps({ type: 'add' })} title="Add Step" />
              </div>
            </div>
          )}
        </div>
        <Button className="ml-6" onClick={handleSwitchConfigChange({ type: 'addCase' })} title="+" />
      </div>
    </>
  );
};

const CaseHolder = ({
  caseItem,
  idx,
  dispatch,
  handleSwitchConfigChange
}: {
  caseItem: Case;
  idx: number;
  dispatch: React.Dispatch<CaseStateAction>;
  handleSwitchConfigChange: any;
}) => {
  const [returns, setReturns] = useState(Array.isArray(caseItem.returns) ? 'advanced' : 'constant');
  const [steps, dispatchSteps] = useTemplateSteps(Array.isArray(caseItem.returns) ? caseItem.returns : []);
  const {
    referenceData: { rows: referenceRows },
    firstDataLine,
    fieldOptions: dropDownOptions
  } = useContext(TemplateEntityContext);
  const { data: preview } = useTemplateStepsPreview(firstDataLine, steps, referenceRows);

  useEffect(() => {
    dispatch({ type: 'setReturns', idx, val: steps });
  }, [dispatch, idx, steps]);

  return (
    <div key={`advanced-case-holder-${idx}}`} className="flex flex-col mt-0 w-full ">
      <div className="flex mt-0 w-full">
        <div className="flex-col mt-6 w-1/4 mr-6">
          If Value
          <ArgsSelect
            maxVisible={2}
            options={options}
            onChange={handleSwitchConfigChange({ type: 'setOperator', idx })}
            value={caseItem.compare[0].operator}
          />
        </div>
        <div className="flex-col mt-6 w-1/3 mr-6">
          Compare To
          <div className="mt-5">
            <ArgsInput
              initialValue={caseItem.compare[0].compareTo as string}
              onChange={handleSwitchConfigChange({ type: 'setCompareTo', idx })}
            />
          </div>
        </div>
        <div className="flex-col mt-6 w-1/3">
          Return
          <ArgsSelect
            maxVisible={2}
            options={inputTypeOptions}
            onChange={(val) => setReturns(val.toString())}
            value={returns}
          />
        </div>
      </div>
      <div className="flex flex-row mt-5 w-full justify-end">
        <div className="flex flex-col mt-0 w-2/3 mr-6">
          {returns === 'constant' ? (
            <div data-testid="advanced-switch-constant-input" className="flex flex-row mt-0 w-full mr-0 justify-end">
              <ArgsInput
                initialValue={Array.isArray(caseItem.returns) ? '' : (caseItem.returns as string)}
                onChange={handleSwitchConfigChange({ type: 'setReturns', idx })}
              />
            </div>
          ) : (
            <div data-testid="advanced-switch-step-holder">
              {steps.map((step, idx) => (
                <StepHolder
                  key={`advanced-${idx}-${step.operation}-${step.args[0]}`}
                  index={idx}
                  step={step}
                  onUpdate={(data) => dispatchSteps({ type: 'update', index: idx, data })}
                  onRemove={() => dispatchSteps({ type: 'remove', index: idx })}
                  dropDownOptions={dropDownOptions}
                  preview={preview?.steps[idx] || 'No Preview'}
                  ignoreAdvanced
                />
              ))}
              <div className="flex gap-4">
                <Button id="add-step-advanced" onClick={() => dispatchSteps({ type: 'add' })} title="Add Step" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvancedSwitch;
